import React, { useCallback } from 'react';

import Input from '@/components/Input';
import { Provider } from '@/models/gen/graphql';
import Table from '@/common/Table';
import useClientsTableState from '@/features/Clients/components/ClientsTable/hook';

export type ClientsTableHeaderProps = {
  data: Provider[];
  selected: string[];
  onSelect: (ids: string | string[]) => void;
};

/**
 * Header row for the clients table.
 * Provides a select-all checkbox and sortable column labels.
 */
const ClientsTableHeader = ({ data, selected, onSelect }: ClientsTableHeaderProps): React.JSX.Element => {
  const [{ sortBy, sortDirection }, setState] = useClientsTableState(({ state, setState }) => [
    { sortBy: state.sorting.column, sortDirection: state.sorting.direction },
    setState,
  ]);

  const handleSort = useCallback(
    (column: string) =>
      (event: React.MouseEvent<HTMLTableCellElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        const isSameColumn = sortBy === column;
        let newDirection: 'asc' | 'desc' | undefined;
        if (!isSameColumn) newDirection = 'asc';
        else if (sortDirection === 'asc') newDirection = 'desc';
        else if (sortDirection === 'desc') newDirection = undefined;
        else newDirection = 'asc';
        setState((current) => ({ ...current, sorting: { column, direction: newDirection } }));
      },
    [sortBy, sortDirection, setState]
  );

  const sorting = useCallback(
    (key: string) => ({ direction: sortBy === key ? sortDirection : undefined, onSort: handleSort(key) }),
    [sortBy, sortDirection, handleSort]
  );

  const isSelectAll = data.length > 0 && selected.length === data.length;
  const handleSelectAll = useCallback((): void => {
    onSelect(data.map((row): string => row.id));
  }, [data, onSelect]);

  return (
    <Table.Row header>
      <Table.Cell className="SELECT">
        <Input type="checkbox" name="SELECT_ALL_CLIENTS" checked={isSelectAll} onChange={handleSelectAll} />
      </Table.Cell>
      <Table.Cell className="NAME alternate" sorting={sorting('name')}>
        Client
      </Table.Cell>
      <Table.Cell className="DISPLAY_NAME" sorting={sorting('displayName')}>
        Short Name
      </Table.Cell>
      <Table.Cell className="CONTACT_PERSON alternate" sorting={sorting('contactPerson')}>
        Contact Person
      </Table.Cell>
      <Table.Cell className="PRIMARY_EMAIL" sorting={sorting('primaryEmail')}>
        Email
      </Table.Cell>
      <Table.Cell className="PRIMARY_PHONE alternate" sorting={sorting('primaryPhoneNumber')}>
        Phone
      </Table.Cell>
      <Table.Cell className="TYPE" sorting={sorting('type')}>
        Type
      </Table.Cell>
      <Table.Cell className="ACTIVE" sorting={sorting('active')}>
        Active
      </Table.Cell>
    </Table.Row>
  );
};

export default React.memo(ClientsTableHeader);
